/**
 * Plugin config field schemas — drives the inline config editor in PluginsTab.
 * Each field declares its type so the editor renders the correct input widget.
 */

export type PluginFieldType = 'text' | 'url' | 'textarea' | 'boolean' | 'number' | 'select' | 'color';

export interface PluginFieldOption {
  value: string;
  label: string;
}

export interface PluginConfigField {
  key: string;
  label: string;
  type: PluginFieldType;
  description?: string;
  options?: PluginFieldOption[];
}

export type PluginConfigSchema = PluginConfigField[];

const PLUGIN_CONFIG_SCHEMAS: Record<string, PluginConfigSchema> = {
  'seo-settings': [
    { key: 'metaTitle',         label: 'Meta Title',           type: 'text',    description: 'Page title shown in browser tabs and search results' },
    { key: 'metaDescription',   label: 'Meta Description',     type: 'textarea',description: 'Short summary for search engine snippets (≤160 chars)' },
    { key: 'ogTitle',           label: 'OG Title',             type: 'text',    description: 'Open Graph title for social sharing' },
    { key: 'ogDescription',     label: 'OG Description',       type: 'textarea',description: 'Open Graph description for social sharing' },
    { key: 'ogImage',           label: 'OG Image URL',         type: 'url',     description: 'Image shown when shared on social media' },
    { key: 'robotsDirective',   label: 'Robots Directive',     type: 'select',  options: [
      { value: 'index',         label: 'index (default)' },
      { value: 'noindex',       label: 'noindex' },
      { value: 'noindex,nofollow', label: 'noindex, nofollow' },
    ]},
    { key: 'canonicalUrl',      label: 'Canonical URL',        type: 'url',     description: 'Canonical link to prevent duplicate content' },
    { key: 'slugUrl',           label: 'Slug / URL Path',      type: 'text',    description: 'URL-friendly path segment' },
    { key: 'faviconUrl',        label: 'Favicon URL',          type: 'url',     description: 'Custom favicon for this form page' },
  ],

  'analytics-settings': [
    { key: 'googleAnalyticsId',     label: 'Google Analytics ID',         type: 'text', description: 'GA4 measurement ID (G-XXXXXXXXXX)' },
    { key: 'googleTagManagerId',    label: 'Google Tag Manager ID',       type: 'text', description: 'GTM container ID (GTM-XXXXXX)' },
    { key: 'facebookPixelId',       label: 'Facebook Pixel ID',           type: 'text' },
    { key: 'linkedInInsightTagId',  label: 'LinkedIn Insight Tag ID',     type: 'text' },
    { key: 'conversionEventName',   label: 'Conversion Event Name',       type: 'text', description: 'Event fired on successful submission' },
    { key: 'utmCaptureEnabled',     label: 'Capture UTM Parameters',      type: 'boolean' },
    { key: 'trackPartialSubmissions', label: 'Track Partial Submissions', type: 'boolean' },
  ],

  'security-settings': [
    { key: 'captchaType',            label: 'CAPTCHA Type',           type: 'select', options: [
      { value: 'none',       label: 'None' },
      { value: 'recaptcha2', label: 'reCAPTCHA v2' },
      { value: 'recaptcha3', label: 'reCAPTCHA v3' },
      { value: 'hcaptcha',   label: 'hCaptcha' },
      { value: 'turnstile',  label: 'Cloudflare Turnstile' },
    ]},
    { key: 'honeypotEnabled',         label: 'Honeypot Spam Protection', type: 'boolean' },
    { key: 'passwordProtected',       label: 'Password Protected',       type: 'boolean' },
    { key: 'formPassword',            label: 'Form Password',            type: 'text',    description: 'Shown only when Password Protected is enabled' },
    { key: 'submissionsPerIpLimit',   label: 'Max Submissions per IP',   type: 'number',  description: '0 = unlimited' },
    { key: 'rateLimitWindowMinutes',  label: 'Rate Limit Window (min)',  type: 'number' },
    { key: 'closedAfterDate',         label: 'Close After Date',         type: 'text',    description: 'ISO date — form stops accepting responses after this date' },
    { key: 'closedAfterCount',        label: 'Close After N Submissions', type: 'number', description: '0 = unlimited' },
  ],

  'compliance-settings': [
    { key: 'gdprConsentEnabled', label: 'GDPR Consent Checkbox',  type: 'boolean' },
    { key: 'gdprConsentText',    label: 'GDPR Consent Text',       type: 'textarea' },
    { key: 'dataRetentionDays',  label: 'Data Retention (days)',   type: 'number',  description: '0 = retain indefinitely' },
    { key: 'hipaaMode',          label: 'HIPAA Mode',              type: 'boolean' },
    { key: 'auditTrailEnabled',  label: 'Audit Trail',             type: 'boolean' },
    { key: 'privacyPolicyUrl',   label: 'Privacy Policy URL',      type: 'url' },
    { key: 'termsUrl',           label: 'Terms & Conditions URL',  type: 'url' },
  ],

  'branding-settings': [
    { key: 'logoUrl',             label: 'Logo URL',             type: 'url' },
    { key: 'logoPosition',        label: 'Logo Position',        type: 'select', options: [
      { value: 'left',   label: 'Left' },
      { value: 'center', label: 'Center' },
      { value: 'right',  label: 'Right' },
    ]},
    { key: 'primaryColor',        label: 'Primary Color',        type: 'color' },
    { key: 'accentColor',         label: 'Accent Color',         type: 'color' },
    { key: 'backgroundColor',     label: 'Background Color',     type: 'color' },
    { key: 'fontFamily',          label: 'Font Family',          type: 'text',    description: 'CSS font-family value or Google Fonts name' },
    { key: 'headerHtml',          label: 'Header HTML',          type: 'textarea',description: 'Injected above the form' },
    { key: 'footerHtml',          label: 'Footer HTML',          type: 'textarea',description: 'Injected below the form' },
    { key: 'hidePoweredByBadge',  label: 'Hide Powered-by Badge', type: 'boolean' },
  ],

  'behavior-settings': [
    { key: 'confirmationType',       label: 'Confirmation Type',        type: 'select', options: [
      { value: 'message',  label: 'Show Message' },
      { value: 'redirect', label: 'Redirect to URL' },
      { value: 'hide',     label: 'Hide Form' },
    ]},
    { key: 'confirmationMessage',    label: 'Confirmation Message',     type: 'textarea' },
    { key: 'redirectUrl',            label: 'Redirect URL',             type: 'url' },
    { key: 'redirectDelaySeconds',   label: 'Redirect Delay (seconds)', type: 'number' },
    { key: 'allowMultipleSubmissions', label: 'Allow Multiple Submissions', type: 'boolean' },
    { key: 'autoSaveEnabled',        label: 'Auto-Save Progress',       type: 'boolean' },
    { key: 'showProgressBar',        label: 'Show Progress Bar',        type: 'boolean' },
  ],

  'notification-settings': [
    { key: 'notificationSubject',       label: 'Notification Subject',        type: 'text',    description: 'Email subject for admin notifications' },
    { key: 'autoResponderEnabled',      label: 'Auto-Responder',              type: 'boolean' },
    { key: 'autoResponderEmailField',   label: 'Respondent Email Field ID',   type: 'text',    description: 'Field ID that captures the respondent email' },
    { key: 'autoResponderSubject',      label: 'Auto-Responder Subject',      type: 'text' },
  ],

  'embed-settings': [
    { key: 'embedType',         label: 'Embed Type',          type: 'select', options: [
      { value: 'inline',  label: 'Inline' },
      { value: 'popup',   label: 'Popup' },
      { value: 'slider',  label: 'Slider' },
      { value: 'fullscreen', label: 'Full Screen' },
    ]},
    { key: 'popupTrigger',      label: 'Popup Trigger',       type: 'select', options: [
      { value: 'load',   label: 'On Page Load' },
      { value: 'scroll', label: 'On Scroll' },
      { value: 'exit',   label: 'On Exit Intent' },
      { value: 'delay',  label: 'After Delay' },
    ]},
    { key: 'popupDelaySeconds', label: 'Popup Delay (seconds)', type: 'number' },
    { key: 'iframeAutoResize',  label: 'iframe Auto-Resize',  type: 'boolean' },
    { key: 'shareLinkEnabled',  label: 'Enable Share Link',   type: 'boolean' },
    { key: 'qrCodeEnabled',     label: 'Generate QR Code',    type: 'boolean' },
  ],

  'custom-code': [
    { key: 'headHtml',      label: '<head> HTML',       type: 'textarea', description: 'Injected inside <head> (scripts, meta tags)' },
    { key: 'bodyStartHtml', label: 'Body Start HTML',   type: 'textarea', description: 'Injected at the start of <body>' },
    { key: 'bodyEndHtml',   label: 'Body End HTML',     type: 'textarea', description: 'Injected at the end of <body>' },
    { key: 'inlineStyles',  label: 'Inline CSS',        type: 'textarea', description: 'Raw CSS injected into <style> in <head>' },
  ],
};

export function getPluginConfigSchema(type: string): PluginConfigSchema {
  return PLUGIN_CONFIG_SCHEMAS[type] ?? [];
}
